import {Tailwind} from '@wandb/weave/components/Tailwind';
import React from 'react';
import {useParams} from 'react-router';
import {twMerge} from 'tailwind-merge';

import {useRampFlagCoreweaveIssues} from '../../state/issues/useCoreweaveIssues';
import {useRampFlagMetricsSearchAPI} from '../../util/rampFeatureFlags';
import {AddPanels} from './AddPanels/AddPanels';
import {INITIAL_DRAWER_WIDTH_CLASS_NAME} from './constants';
import {WorkspaceCreateTriggerDrawerWrapper} from './CreateTrigger/WorkspaceCreateTriggerDrawerWrapper';
import {FloatingWorkspaceDrawer} from './FloatingWorkspaceDrawer';
import {InlineWorkspaceDrawer} from './InlineWorkspaceDrawer';
import {PanelsQuickAdd} from './PanelsQuickAdd/PanelsQuickAdd';
import {PanelsQuickAddPaginated} from './PanelsQuickAdd/PanelsQuickAddPaginated';
import {RunIssuesDrawer} from './RunIssues/RunIssuesDrawer';
import {MediaSettingsDrawer} from './Settings/media/MediaSettingsDrawer';
import {SectionSettings} from './Settings/SectionSettings';
import {WorkspaceSettingsContent} from './Settings/WorkspaceSettingsContent';
import {useWorkspaceDrawerContext} from './WorkspaceDrawerContextProvider';

export const WorkspaceDrawer = () => {
  const {closeWorkspaceDrawer, contentType, drawerType} =
    useWorkspaceDrawerContext();

  const isDrawerOpen = contentType != null;

  if (drawerType === 'inline') {
    return (
      <InlineWorkspaceDrawer isDrawerOpen={isDrawerOpen}>
        <div
          className={twMerge(
            'flex h-full flex-col',
            'bg-white',
            'border-l border-moon-250 ',
            INITIAL_DRAWER_WIDTH_CLASS_NAME
          )}>
          <div className="sticky top-0" data-test="workspace-drawer">
            <WorkspaceDrawerContent />
          </div>
        </div>
      </InlineWorkspaceDrawer>
    );
  }

  return (
    <Tailwind>
      <FloatingWorkspaceDrawer
        isDrawerOpen={isDrawerOpen}
        closeWorkspaceDrawer={closeWorkspaceDrawer}>
        <div className="h-full w-full" data-test="workspace-drawer">
          <WorkspaceDrawerContent />
        </div>
      </FloatingWorkspaceDrawer>
    </Tailwind>
  );
};

export const WorkspaceDrawerContent = () => {
  const {contentType, delayedContentType, targetPanelRef, targetSectionRef} =
    useWorkspaceDrawerContext();
  const {entityName} = useParams<{
    entityName: string;
  }>();
  const isIssuesObservabilityEnabled = useRampFlagCoreweaveIssues();

  // to ensure the drawer contents don't abruptly disappear during the
  // "drawer closing" transition, we must use delayedContentType so the
  // contents remain visible until the drawer is fully closed.
  const displayedContentType = contentType ?? delayedContentType;

  const useMetricsSearchAPI = useRampFlagMetricsSearchAPI(entityName);

  if (displayedContentType === 'add-panels') {
    return <AddPanels />;
  }

  if (displayedContentType === 'run-issues' && isIssuesObservabilityEnabled) {
    return <RunIssuesDrawer />;
  }

  if (displayedContentType === 'panels-quick-add') {
    return useMetricsSearchAPI ? (
      <PanelsQuickAddPaginated />
    ) : (
      <PanelsQuickAdd />
    );
  }

  if (displayedContentType === 'workspace-settings') {
    return (
      <WorkspaceSettingsContent displayedContentType={displayedContentType} />
    );
  }

  if (
    displayedContentType === 'media-settings' &&
    targetPanelRef != null &&
    targetSectionRef != null
  ) {
    return (
      <MediaSettingsDrawer
        panelRef={targetPanelRef}
        panelBankSectionConfigRef={targetSectionRef}
      />
    );
  }

  if (displayedContentType === 'section-settings') {
    if (targetSectionRef == null) {
      // This shouldn't happen, since we're in the section settings drawer
      return null;
    }
    return (
      <SectionSettings
        displayedContentType={displayedContentType}
        sectionRef={targetSectionRef}
      />
    );
  }

  if (displayedContentType === 'create-trigger') {
    return <WorkspaceCreateTriggerDrawerWrapper />;
  }

  return null;
};
